/**
 * ADR-0137 — pure helpers for the data-driven agent-role scope editor.
 *
 * Scope metadata comes from the hub's `available_scopes` (the canonical scope
 * registry). These functions drive the role-editor checkboxes and the scope
 * pills so a new tool family surfaces in the console by existing on the hub —
 * no console edit. Kept here (pure, no React) so they're unit-tested.
 */

export type AgentScopeMeta = {
  value: string;
  label: string;
  family: string;
  family_label: string;
  description: string;
  selection: "exclusive" | "multi";
};

/**
 * Scope pill label — `<family label> · <action>`, action taken from the
 * registry label's head (the text before its parenthetical), so the pill says
 * exactly what the editor row says: "Tenant Knowledge · Read + propose + write".
 * Falls back to the raw value only if the scope isn't in the registry (should
 * not happen).
 */
export function scopePillLabel(scope: string, scopeMeta: AgentScopeMeta[]): string {
  const m = scopeMeta.find((s) => s.value === scope);
  if (!m) return scope;
  const action = m.label.split(" (")[0]!.trim();
  return `${m.family_label} · ${action}`;
}

/** Group available scopes by family, preserving first-seen (registry) order. */
export function groupScopesByFamily(
  scopes: AgentScopeMeta[]
): Array<{ family: string; family_label: string; scopes: AgentScopeMeta[] }> {
  const groups: Array<{ family: string; family_label: string; scopes: AgentScopeMeta[] }> = [];
  const byFamily = new Map<string, { family: string; family_label: string; scopes: AgentScopeMeta[] }>();
  for (const s of scopes) {
    let g = byFamily.get(s.family);
    if (!g) {
      g = { family: s.family, family_label: s.family_label, scopes: [] };
      byFamily.set(s.family, g);
      groups.push(g);
    }
    g.scopes.push(s);
  }
  return groups;
}

/**
 * Toggle a scope in the editor selection, honoring each scope's selection mode.
 * `exclusive` scopes are a family's tiers (e.g. Tenant Knowledge: Read, Read +
 * propose, Read + write) — at most one is held, and selecting one clears the
 * family's other tiers. `multi` scopes are independent checkboxes — including
 * an additive grant that sits under an exclusive family's heading (Studies ·
 * Redact), which a tier switch must never clear.
 */
export function toggleScopeSelection(
  current: string[],
  scope: AgentScopeMeta,
  allScopes: AgentScopeMeta[],
  checked: boolean
): string[] {
  const set = new Set(current);
  if (scope.selection === "exclusive") {
    for (const s of allScopes) {
      if (s.family === scope.family && s.selection === "exclusive") set.delete(s.value);
    }
    if (checked) set.add(scope.value);
  } else if (checked) {
    set.add(scope.value);
  } else {
    set.delete(scope.value);
  }
  return [...set];
}
